import { IColumn } from '@fluentui/react';
import { useTheme } from '@fluentui/react/lib/Theme';
import AppContext from 'App/AppContext';
import {
  IPCheckbox,
  IPDetailsList,
  IPDropdown,
  IPMessageBar,
  IPMessageBarType,
  IPScrollablePane,
  IPScrollbarVisibility,
  IPSpinner,
  IPText,
  IPTooltipHost,
} from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectableOption, IPSelectableOptionMenuItemType } from 'baseComponents/Utils/SelectableOption';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import GenericModal from 'components/Dialogs/GenericModal';
import { getErrorMessageText } from 'components/Notification/ErrorMessageService';
import { onRenderDetailsHeaderGlobal } from 'globalFunctions';
import { globalStackTokensGapSmall } from 'globalStyles';
import AuditCriteriaSet, {
  auditCriterionVerificationAI,
  auditCriterionVerificationManual,
  getAuditCriterionStateText,
} from 'models/auditCriteria';
import Task, { TaskTypes } from 'models/tasks/task';
import {
  TemplateCriteriaImportPreview,
  templateCriteriaImportReasonAttachment,
  templateCriteriaImportReasonComputed,
  templateCriteriaImportReasonContext,
  templateCriteriaImportReasonNoText,
  templateCriteriaImportReasonRetired,
  TemplateCriteriaImportRequest,
  TemplateCriteriaImportRow,
  templateCriteriaImportSourceChecklist,
  templateCriteriaImportStatusDuplicateInImport,
  templateCriteriaImportStatusDuplicateInSet,
  templateCriteriaImportStatusNew,
  templateCriteriaImportStatusNotConverted,
} from 'models/templateCriteriaImport';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiImportTemplateCriteria, apiPreviewTemplateCriteriaImport } from 'services/Api/auditCriteriaService';
import { apiGetTaskTemplates } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';

interface ITemplateCriteriaImportPanelProps {
  isOpen: boolean;
  // the generic set the criteria are added to
  auditCriteriaSetId: number;
  // the set's family and subtype, as the generic modal names it
  targetName: string;
  onClose: () => void;
  // the set after the import, so the editor shows the new criteria
  onImported: (set: AuditCriteriaSet | undefined) => void;
  // a template chosen beforehand (the templates overview's upgrade, PLAN-CTI-085): preselected, the choice locked
  initialTaskId?: number;
  // the source itself when it is not a template (a recurring annual plan task, PLAN-CTI-085 A1): the templates are not
  // read, the one source is preselected and locked; the preview and the add take its id as they take a template's
  source?: Task;
}

const hasChecklist = (template: Task): boolean => template.checkList.items.length > 0;

const hasForm = (template: Task): boolean => !!template.taskTypeId;

/**
 * "Add from template" in the generic criteria editor (PLAN-CTI-081, spec 7.4): a task or event template's checklist and
 * form become criteria of the set. Every item of the template is previewed with what it becomes, or why it is not
 * added (already in the set by name, twice in the template, not a question); the add appends the new ones and keeps
 * the skipped ones on screen until the dialog is closed. A modal, not a Fluent panel: a panel opens behind the generic
 * criteria modal. The upgrade wizard opens it with its template preselected (initialTaskId), or with a recurring annual
 * plan task as the one source (source).
 */
const TemplateCriteriaImportPanel = (props: ITemplateCriteriaImportPanelProps) => {
  const { t } = useTranslation(['criteria', 'translation', 'exceptions']);
  const appContext = useContext(AppContext);
  const theme = useTheme();
  const [templates, setTemplates] = useState<Task[] | undefined>(undefined);
  const [request, setRequest] = useState<TemplateCriteriaImportRequest>(() => new TemplateCriteriaImportRequest());
  const [preview, setPreview] = useState<TemplateCriteriaImportPreview | undefined>(undefined);
  const [isPreviewing, setIsPreviewing] = useState<boolean>(false);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  // after the add: the rows that were not added, kept until the dialog closes
  const [skipped, setSkipped] = useState<TemplateCriteriaImportRow[] | undefined>(undefined);
  // only the answer to the latest options is shown
  const previewRequestRef = useRef<number>(0);

  const getErrorText = (err: unknown): string =>
    err instanceof AppError ? getErrorMessageText(err, t) : t('exceptions:General.UnknownError');

  useEffect(() => {
    if (!props.isOpen) return;

    const initial = new TemplateCriteriaImportRequest();
    initial.taskId = props.source?.taskId ?? props.initialTaskId ?? 0;
    setRequest(initial);
    setPreview(undefined);
    setSkipped(undefined);
    if (props.source) {
      setTemplates([props.source]);

      return;
    }

    const load = async () => {
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const scene = await apiGetTaskTemplates(accessToken, appContext.globalDataCache);
        setTemplates(
          scene.tasks
            .filter((template) => hasChecklist(template) || hasForm(template))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      } catch (err) {
        setTemplates([]);
        appContext.showNotification(getErrorText(err), true, false, true);
      }
    };
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  const selectedTemplate = templates?.find((template) => template.taskId === request.taskId);

  // the preview follows every change of the options
  useEffect(() => {
    if (!props.isOpen || !request.taskId || skipped) return;

    const requestNumber = ++previewRequestRef.current;
    const load = async () => {
      try {
        setIsPreviewing(true);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const loaded = await apiPreviewTemplateCriteriaImport(props.auditCriteriaSetId, request, accessToken);
        if (requestNumber === previewRequestRef.current) setPreview(loaded);
      } catch (err) {
        if (requestNumber === previewRequestRef.current) {
          setPreview(undefined);
          appContext.showNotification(getErrorText(err), true, false, true);
        }
      } finally {
        if (requestNumber === previewRequestRef.current) setIsPreviewing(false);
      }
    };
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.auditCriteriaSetId, request]);

  const changeRequest = (change: (next: TemplateCriteriaImportRequest) => void) => {
    const next = Object.assign(new TemplateCriteriaImportRequest(), request);
    change(next);
    setRequest(next);
  };

  const add = async () => {
    if (!preview || preview.newCount === 0) return;

    try {
      setIsAdding(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiImportTemplateCriteria(props.auditCriteriaSetId, request, accessToken);
      const notAdded = result.preview.rows.filter((row) => row.status !== templateCriteriaImportStatusNew);
      appContext.showNotification(
        t('criteria:TemplateImport.Added', { added: result.preview.newCount, skipped: notAdded.length }),
        false,
        false,
        true,
      );
      props.onImported(result.set);
      if (notAdded.length === 0) {
        props.onClose();

        return;
      }
      setSkipped(notAdded);
    } catch (err) {
      appContext.showNotification(getErrorText(err), true, false, true);
    } finally {
      setIsAdding(false);
    }
  };

  const getTemplateOptionText = (template: Task): string => {
    if (hasChecklist(template) && hasForm(template)) {
      return t('criteria:TemplateImport.TemplateOption.Both', {
        name: template.name,
        n: template.checkList.items.length,
      });
    }

    return hasChecklist(template)
      ? t('criteria:TemplateImport.TemplateOption.Checklist', {
          name: template.name,
          n: template.checkList.items.length,
        })
      : t('criteria:TemplateImport.TemplateOption.Form', { name: template.name });
  };

  // task templates and event templates, each under its own header
  const templateOptions: IPSelectableOption[] = [
    { type: TaskTypes.Template, header: t('criteria:TemplateImport.Group.Task') },
    { type: TaskTypes.EventTemplate, header: t('criteria:TemplateImport.Group.Event') },
    // a recurring annual plan task, only ever the given source
    { type: TaskTypes.Monitoring, header: t('criteria:TemplateImport.Group.Series') },
  ].flatMap((group) => {
    const items = (templates ?? []).filter((template) => template.taskType === group.type);
    if (items.length === 0) return [];

    return [
      { key: `header-${group.type}`, text: group.header, itemType: IPSelectableOptionMenuItemType.Header },
      ...items.map((template) => ({ key: template.taskId, text: getTemplateOptionText(template) })),
    ];
  });

  const verificationOptions: IPSelectableOption[] = [
    { key: auditCriterionVerificationAI, text: t('criteria:Verification.AI') },
    { key: auditCriterionVerificationManual, text: t('criteria:Verification.Manual') },
  ];

  const getStatusText = (status: string): string => {
    switch (status) {
      case templateCriteriaImportStatusNew:
        return t('criteria:TemplateImport.Status.New');
      case templateCriteriaImportStatusDuplicateInSet:
        return t('criteria:TemplateImport.Status.DuplicateInSet');
      case templateCriteriaImportStatusDuplicateInImport:
        return t('criteria:TemplateImport.Status.DuplicateInImport');
      default:
        return t('criteria:TemplateImport.Status.NotConverted');
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case templateCriteriaImportStatusNew:
        return theme.semanticColors.successIcon;
      case templateCriteriaImportStatusNotConverted:
        return theme.semanticColors.errorText;
      default:
        return theme.semanticColors.warningIcon;
    }
  };

  // the reason of a row that is not added: a skip code in the user's language, the twin in the set, or the server's text
  const getReasonText = (row: TemplateCriteriaImportRow): string => {
    if (row.status === templateCriteriaImportStatusDuplicateInSet) {
      return t('criteria:TemplateImport.ExistingState', {
        key: row.existingCriterionKey,
        state: getAuditCriterionStateText(row.existingState, t),
      });
    }
    switch (row.reason) {
      case templateCriteriaImportReasonComputed:
        return t('criteria:TemplateImport.Reason.Computed');
      case templateCriteriaImportReasonAttachment:
        return t('criteria:TemplateImport.Reason.Attachment');
      case templateCriteriaImportReasonContext:
        return t('criteria:TemplateImport.Reason.Context');
      case templateCriteriaImportReasonRetired:
        return t('criteria:TemplateImport.Reason.Retired');
      case templateCriteriaImportReasonNoText:
        return t('criteria:TemplateImport.Reason.NoText');
      default:
        return (
          row.reason || (row.status === templateCriteriaImportStatusDuplicateInImport ? getDuplicateOfText(row) : '')
        );
    }
  };

  // a duplicate in the import names the item of the template that wins
  const getDuplicateOfText = (row: TemplateCriteriaImportRow): string => {
    if (!row.duplicateOfSourceIndex) return t('criteria:TemplateImport.DuplicateOf');

    return row.duplicateOfSource === templateCriteriaImportSourceChecklist
      ? t('criteria:TemplateImport.DuplicateOfChecklist', { n: row.duplicateOfSourceIndex })
      : t('criteria:TemplateImport.DuplicateOfForm', { n: row.duplicateOfSourceIndex });
  };

  const getSourceText = (row: TemplateCriteriaImportRow): string =>
    row.source === templateCriteriaImportSourceChecklist
      ? t('criteria:TemplateImport.Source.Checklist', { n: row.sourceIndex })
      : t('criteria:TemplateImport.Source.Form', { name: row.sourceName });

  const renderWrapped = (text: string) => (
    <IPTooltipHost content={text}>
      <IPText block nowrap>
        {text}
      </IPText>
    </IPTooltipHost>
  );

  const columns: IColumn[] = [
    {
      key: 'status',
      name: t('criteria:TemplateImport.Columns.Status'),
      minWidth: 110,
      maxWidth: 140,
      onRender: (row: TemplateCriteriaImportRow) => (
        <IPText styles={{ root: { color: getStatusColor(row.status), fontWeight: 600 } }}>
          {getStatusText(row.status)}
        </IPText>
      ),
    },
    {
      key: 'requirement',
      name: t('criteria:TemplateImport.Columns.Requirement'),
      minWidth: 220,
      isResizable: true,
      onRender: (row: TemplateCriteriaImportRow) => renderWrapped(row.requirement || row.sourceName),
    },
    {
      key: 'columnName',
      name: t('criteria:TemplateImport.Columns.ColumnName'),
      minWidth: 110,
      maxWidth: 160,
      isResizable: true,
      onRender: (row: TemplateCriteriaImportRow) => renderWrapped(row.columnName),
    },
    {
      key: 'successCriteria',
      name: t('criteria:TemplateImport.Columns.SuccessCriteria'),
      minWidth: 200,
      isResizable: true,
      onRender: (row: TemplateCriteriaImportRow) => renderWrapped(row.successCriteria),
    },
    {
      key: 'source',
      name: t('criteria:TemplateImport.Columns.Source'),
      minWidth: 120,
      maxWidth: 180,
      isResizable: true,
      onRender: (row: TemplateCriteriaImportRow) => renderWrapped(getSourceText(row)),
    },
    {
      key: 'reason',
      name: t('criteria:TemplateImport.Columns.Reason'),
      minWidth: 180,
      isResizable: true,
      onRender: (row: TemplateCriteriaImportRow) => renderWrapped(getReasonText(row)),
    },
  ];

  const rows = skipped ?? preview?.rows ?? [];
  const newCount = skipped ? 0 : preview?.newCount ?? 0;

  return (
    <GenericModal
      isOpen={props.isOpen}
      isBlocking
      onClose={props.onClose}
      title={t('criteria:TemplateImport.Title')}
      subTitle={t('criteria:TemplateImport.Target', { target: props.targetName })}
      onSave={skipped ? undefined : add}
      keepOpenAfterSave={true}
      isActionPending={isAdding}
      saveButtonText={t('criteria:TemplateImport.Add', { n: newCount })}
      saveDisabled={newCount === 0 || isPreviewing || isAdding}
      closeButtonText={skipped ? t('criteria:Close') : t('translation:General.Button.Cancel')}
      width="85vw"
      maxWidth={1200}
      height="85vh"
      growContent={true}
    >
      <IPStack tokens={globalStackTokensGapSmall} verticalFill styles={{ root: { minHeight: 0 } }}>
        {templates === undefined ? (
          <IPSpinner label={t('translation:General.Notifications.Loading')} />
        ) : templates.length === 0 ? (
          <IPText>{t('criteria:TemplateImport.NoTemplates')}</IPText>
        ) : (
          <>
            <IPDropdown
              label={t('criteria:TemplateImport.Template')}
              placeholder={t('criteria:TemplateImport.TemplatePlaceholder')}
              options={templateOptions}
              selectedKey={request.taskId || null}
              disabled={isAdding || !!skipped || props.initialTaskId !== undefined || props.source !== undefined}
              onChange={(_, option) =>
                changeRequest((next) => {
                  next.taskId = Number(option?.key ?? 0);
                  next.includeChecklist = true;
                  next.includeForm = true;
                })
              }
            />
            {selectedTemplate && (
              <IPStack horizontal wrap verticalAlign="end" tokens={{ childrenGap: 24 }}>
                {hasChecklist(selectedTemplate) && (
                  <IPCheckbox
                    label={t('criteria:TemplateImport.IncludeChecklist')}
                    checked={request.includeChecklist}
                    disabled={isAdding || !!skipped}
                    onChange={(_, checked) => changeRequest((next) => (next.includeChecklist = checked === true))}
                  />
                )}
                {hasForm(selectedTemplate) && (
                  <IPCheckbox
                    label={t('criteria:TemplateImport.IncludeForm')}
                    checked={request.includeForm}
                    disabled={isAdding || !!skipped}
                    onChange={(_, checked) => changeRequest((next) => (next.includeForm = checked === true))}
                  />
                )}
                <IPDropdown
                  label={t('criteria:TemplateImport.Verification')}
                  options={verificationOptions}
                  selectedKey={request.verification}
                  disabled={isAdding || !!skipped}
                  onChange={(_, option) =>
                    changeRequest((next) => (next.verification = (option?.key as string) ?? next.verification))
                  }
                />
              </IPStack>
            )}
          </>
        )}
        {skipped && (
          <IPMessageBar messageBarType={IPMessageBarType.info}>{t('criteria:TemplateImport.Skipped')}</IPMessageBar>
        )}
        {!skipped && preview && (
          <IPText>
            {t('criteria:TemplateImport.Counts', {
              new: preview.newCount,
              duplicates: preview.duplicateCount,
              notConverted: preview.notConvertedCount,
            })}
          </IPText>
        )}
        {!skipped && preview && preview.newCount === 0 && !isPreviewing && (
          <IPMessageBar messageBarType={IPMessageBarType.warning}>
            {t('criteria:TemplateImport.NothingToAdd')}
          </IPMessageBar>
        )}
        {isPreviewing ? (
          <IPSpinner label={t('translation:General.Notifications.Loading')} />
        ) : (
          rows.length > 0 && (
            // only the rows scroll: the pane fills this positioned item, so the options and the footer stay
            <IPStack.Item grow styles={{ root: { minHeight: 200, position: 'relative' } }}>
              <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
                <IPDetailsList
                  items={rows}
                  columns={columns}
                  compact
                  setKey="templateImport"
                  getKey={(row: TemplateCriteriaImportRow) => `${row.source}-${row.sourceIndex}`}
                  selectionMode={IPSelectionMode.none}
                  layoutMode={IPDetailsListLayoutMode.justified}
                  onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
                />
              </IPScrollablePane>
            </IPStack.Item>
          )
        )}
      </IPStack>
    </GenericModal>
  );
};

export default TemplateCriteriaImportPanel;
